import { SVButton, SVLoader, SVTable } from '@/components/core';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import { ROUTES } from '@/constants/routes';
import {
  useGetBankAccountDetails,
  useGetStatements,
} from '@/hooks/requests/useBankAccounts';
import { SVTableRow } from '@/types/common';
import { generateBankAccountDisplayName } from '@/utils/bankAccounts';
import { formatDateTimeFromUTC } from '@/utils/date';
import { titleCase } from '@/utils/stringCaser';
import { Card, Typography } from '@mui/material';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

export default function BankAccountsStatementsPage() {
  const [page, setPage] = useState(0);
  const { uuid = '' } = useParams();

  const { statements, totalRecordCount, statementsIsPending } =
    useGetStatements(uuid);
  const { accountDetails, accountsDetailsIsPending } =
    useGetBankAccountDetails(uuid);

  if (statementsIsPending || accountsDetailsIsPending) {
    return (
      <DefaultLayout header="Statements">
        <Card>
          <SVLoader />
        </Card>
      </DefaultLayout>
    );
  }

  if (!accountDetails) {
    return (
      <DefaultLayout header="Statements">
        <Card>
          <Typography>
            Something went wrong retreiving the bank account statements. Our
            team has been notified.
          </Typography>
        </Card>
      </DefaultLayout>
    );
  }

  const rows: SVTableRow[] = statements.map(statement => {
    const { statementDate, statementType, presignedUrl } = statement;

    return {
      key: `documentRow${statementDate}`,
      cells: [
        {
          key: `date${uuid}`,
          type: 'text',
          content: formatDateTimeFromUTC(statementDate, false, 'MMMM DD, YYYY'),
        },
        {
          key: `status${uuid}`,
          type: 'text',
          content: titleCase(statementType),
        },
        {
          key: `url${uuid}`,
          type: 'custom',
          content: presignedUrl ? (
            <SVButton onClick={() => window.open(presignedUrl, '_blank')}>
              Download
            </SVButton>
          ) : (
            <Typography>Statement Currently Unavailable</Typography>
          ),
        },
      ],
    };
  });

  const { applicationType, business, primaryOwner, owners } = accountDetails;
  const { fullName, email } = primaryOwner.profile;
  const { name: businessName } = business || {};
  const totalOwners = owners.length;
  const bankAccountName = generateBankAccountDisplayName({
    applicationType,
    businessName,
    fullName,
    email,
    totalOwners,
  });

  return (
    <DefaultLayout
      header="Statements"
      breadcrumb={[
        { label: 'Bank Accounts', href: ROUTES.BANK_ACCOUNTS },
        { label: bankAccountName, href: `${ROUTES.BANK_ACCOUNTS}/${uuid}` },
        { label: 'Statements', isActive: true },
      ]}>
      <Card>
        <SVTable
          ariaLabel="Statements"
          headers={[
            { key: 'date', content: 'Date' },
            { key: 'type', content: 'Type' },
            { key: 'download', content: '' },
          ]}
          rows={rows}
          emptyTableContent="This bank account has no statements"
          hasPagination
          totalRecordCount={totalRecordCount}
          page={page}
          setPage={setPage}
        />
      </Card>
    </DefaultLayout>
  );
}
